import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePollsContext } from '../../context/PollsContext';
import { useLocaleContext } from '../../context/LocaleContext';
import { useUserContext } from '../../context/UserContext';
import { useEventContext } from '../../context/EventContext';
import { useModalContext } from '../../context/ModalContext';
import { usePlayerContext } from '../../context/PlayerContext';
import { useWidgetContext } from '../../context/WidgetContextProvider';
import storage from '../../../../player/js/storage';
import UserAuthenticationModal from '../User/UserAuthenticationModal';
import ConfirmationModal from '../ConfirmationModal';
import { CButton } from '../UI';
import Poll from './Poll';
import PollResult from './Result';
import PopUp from '../PopUp';

import './index.scss';

export const PollsContent = ({ onAnswer, isResult, preview, eventKey, isPoll, poll, hasNext, authorizationCheck, onCloseResultPoll }) => {
    const { get: getLocale } = useLocaleContext();
    const navigate = useNavigate();
    const { unreadPolls, viewedResultPolls, answeredPollIds, onViewPoll } = usePollsContext();

    const [answered, setAnswered] = useState(false);

    const handleAnswer = (poll, answers) => {
        setAnswered(true);
        onAnswer(poll, answers);
    };

    const openNextPoll = () => {
        onCloseResultPoll(poll);
        onViewPoll(poll);
        navigate(`${window.location.pathname}?view-poll-id=${unreadPolls[0]?.id}`);
    };

    useEffect(() => {
        if (!isPoll && answered && !viewedResultPolls.includes(poll.id)) {
            const result = [...viewedResultPolls, poll.id];
            storage.set(`event/${eventKey}/polls/viewedResult`, JSON.stringify(result), storage.MONTH);
        }
    }, [poll, viewedResultPolls, answered, isPoll]);

    if (preview || (!answeredPollIds.includes(poll.id) && !isResult)) {
        return <Poll poll={poll} preview={preview} onAnswer={handleAnswer} authorizationCheck={authorizationCheck} />;
    }

    return (
        <>
            <PollResult onCloseResultPoll={onCloseResultPoll} poll={poll} />
            {answered && <p className={'poll-empty-label'}>{getLocale('polls.thankYou')}</p>}
            {/* {(answered || poll.private) && <p className={'poll-empty-label'}>{getLocale('polls.thankYou')}</p>} */}
            {hasNext && <div style={{ marginTop: "1rem", display: 'flex', justifyContent: 'center' }}>
                <CButton label={getLocale('polls.nextPoll')} size="lg" theme="accent" onClick={openNextPoll} />
            </div>}
        </>
    );
};

const ModalPolls = ({ isFixedDisplay, outerComponent }) => {
    const { user } = useUserContext();
    const { event } = useEventContext();
    const { renderModal } = useModalContext();
    const $locale = useLocaleContext();
    const { setCurrentModule, mobileView } = usePlayerContext();
    const {
        viewPolls,
        allPolls,
        resultPolls,
        onAnswer,
        onCloseResultPoll,
        answeredPollIds,
        answeredPolls,
        activePolls,
        onViewPoll,
        onClosePoll,
    } = usePollsContext();
    const { isWidget } = useWidgetContext();
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const viewPollId = params.get('view-poll-id');

    const authorizationCheck = (poll) => {
        return new Promise((resolve) => {
            if (poll.authorizationRequired && !user) {
                renderModal(
                    UserAuthenticationModal,
                    { event, $locale, onClose: (props) => resolve(props?.isAuth) },
                    null,
                    true,
                    outerComponent
                );
            } else {
                resolve(true);
            }
        })
    };

    const { poll, isPoll, isResult } = useMemo(() => {
        let poll;
        let isPoll;
        let isResult;

        if (viewPollId && viewPollId !== "active") {
            poll = allPolls?.find(item => String(item.id) === viewPollId);
            isPoll = !answeredPollIds.includes(poll?.id);
        } else {
            isPoll = viewPolls?.length > 0;
            poll = viewPolls[0];
            // poll = isPoll ? viewPolls[0] : resultPolls?.length > 0 ? resultPolls[0] : undefined;
        }
        if (!poll && resultPolls?.length > 0) {
            poll = resultPolls[0];
            isResult = true;
        }

        return { poll, isPoll, isResult };
    }, [allPolls, viewPollId, answeredPollIds, viewPolls, resultPolls]);

    const hasNext = useMemo(() => {
        return answeredPolls.length < activePolls.length;
    }, [activePolls, answeredPolls]);

    const onClose = useCallback(() => {
        if (viewPollId && viewPollId !== "active") {
            navigate(`${location.pathname}`);
        }

        onViewPoll(poll);
        onClosePoll(poll);

        if (!isPoll) {
            onCloseResultPoll(poll);
        }
    }, [poll, isPoll, viewPollId]);

    useEffect(() => {
        if (poll && mobileView) {
            setCurrentModule(null);
        }
    }, [poll, mobileView]);

    useEffect(() => {
        if (!!poll && poll.status === 'active') {
            navigate(`${window.location.pathname}?view-poll-id=${poll.id}`)
        }
    }, [poll]);

    const onCloseCheck = () => {
        return new Promise((resolve) => {
            if (hasNext || (poll && !answeredPollIds.includes(poll.id))) {
                renderModal(
                    ConfirmationModal,
                    {
                        $locale,
                        title: 'polls.confirmationClose.title',
                        onClose: (props) => resolve(props?.isConfirmed),
                        lockPageScroll: false,
                    },
                    null,
                    true,
                    outerComponent
                );
            } else {
                resolve(true);
            }
        });
    };

    const isEmptyTitle = !isPoll && poll && poll.private && 'result' !== poll.status;

    return (
        <PopUp
            title={!isEmptyTitle ? $locale.get(`polls.${isPoll ? 'title' : 'resultTitle'}`) : undefined}
            isMoving
            isOpen={poll}
            isAbsoluteDisplay={!isFixedDisplay}
            isWidget={isWidget}
            onClose={onClose}
            onCloseCheck={onCloseCheck}
        >
            <PollsContent
                eventKey={event.eventKey}
                isResult={isResult}
                onCloseResultPoll={onCloseResultPoll}
                isPoll={isPoll}
                poll={poll}
                hasNext={hasNext}
                onAnswer={onAnswer}
                authorizationCheck={authorizationCheck}
                isAnswered={answeredPollIds.includes(poll?.id)}
            />
        </PopUp>
    )
};

export default React.memo(ModalPolls);
